import {
  APIFieldType,
  APIFieldAttributes,
  APIReferenceAttributes,
  APIComponentAttributes,
  APIAttachment,
  APIOrganizationUser,
  ArdoqId,
} from '@ardoq/api-types';
import {
  CellTypes,
  ComponentProps,
  HeaderModel,
  ReferenceProps,
  TableViewModel,
} from './types';
import { ContextSort, urlUtils } from '@ardoq/common-helpers';

import { dateRangeOperations } from '@ardoq/date-range';
import { filterCache } from 'collections/filterCache';
import { fieldUtils } from '@ardoq/scope-data';
import { usersOperations } from '@ardoq/core';
import { type I18nLocale } from '@ardoq/i18n';
import { getUserImage } from 'streams/orgUsers/orgUsersUtils';

/**
 * Model data resolved upstream in the table view-model streams and threaded
 * into the pure cell formatters so this module never reads global state.
 */
export type TableCustomFieldContext = {
  usersById: Record<ArdoqId, APIOrganizationUser>;
  attachmentsById: Record<ArdoqId, APIAttachment>;
  i18nLocale: I18nLocale;
};

const fieldAppliesToComponent = (
  field: APIFieldAttributes,
  component: APIComponentAttributes
): boolean =>
  Boolean(
    component.typeId &&
    field.model === component.model &&
    field.componentType.includes(component.typeId)
  );

const fieldAppliesToReference = (
  field: APIFieldAttributes,
  reference: APIReferenceAttributes
): boolean =>
  Boolean(
    reference.type &&
    field.model === reference.model &&
    field.referenceType.includes(String(reference.type))
  );

const formatUserFieldValue =
  (usersById: Record<ArdoqId, APIOrganizationUser>) =>
  (value: any): string | null => {
    if (typeof value !== 'string') return null;
    const user = usersOperations.getUserById(usersById, value);
    return user ? usersOperations.getUserDisplayText(user) : null;
  };

export const emptyState: TableViewModel = {
  allDefaultHeaders: [],
  allReferenceTypeHeaders: [],
  allFieldHeaders: [],
  defaultHeaders: [],
  referenceTypeHeaders: [],
  fieldHeaders: [],
  rows: null,
  expandedDescriptions: new Set(),
  hasComponentsAvailable: false,
};

export const componentPropsFromData = (
  data: APIComponentAttributes
): ComponentProps => ({
  entityType: 'component',
  name: data.name,
  id: data._id,
  cid: data._id,
  filterColor: filterCache.getColor(data._id) || '',
  typeId: data.typeId,
  type: data.type || '',
  'component-key': data['component-key'] || '',
  created: data.created,
  'last-updated': data['last-updated'],
});

const componentPropsFromDataOrFallback = (
  componentId: ArdoqId,
  componentsById: Record<ArdoqId, APIComponentAttributes>
): ComponentProps => {
  const data = componentsById[componentId];
  if (data) {
    return componentPropsFromData(data);
  }
  // Fallback for missing components
  return {
    entityType: 'component',
    name: '',
    id: componentId,
    cid: componentId,
    filterColor: '',
    typeId: '',
    type: '',
    'component-key': '',
    created: '',
    'last-updated': '',
  };
};

export const referencePropsFromData = (
  data: APIReferenceAttributes,
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  referenceTypeName: string | null
): ReferenceProps => {
  return {
    entityType: 'reference',
    id: data._id,
    cid: data._id,
    source: componentPropsFromDataOrFallback(data.source, componentsById),
    target: componentPropsFromDataOrFallback(data.target, componentsById),
    displayText: data.displayText || '',
    type: referenceTypeName || '',
    created: data.created,
    'last-updated': data['last-updated'],
  };
};

const getFieldValue = (
  field: APIFieldAttributes,
  data: APIComponentAttributes | APIReferenceAttributes
) => {
  if (dateRangeOperations.isDateRangeField(field)) {
    // For date range fields, the field name is "startField,endField"
    // We need to get the actual start and end field names
    return {
      start: data[field.dateTimeFields.start.name],
      end: data[field.dateTimeFields.end.name],
    };
  }
  return data[field.name];
};

export const extractCustomFieldsFromComponentData = (
  fields: APIFieldAttributes[],
  data: APIComponentAttributes,
  context: TableCustomFieldContext
) => {
  const fieldMap: Record<string, { isDisabled: boolean; value: any }> = {};
  return fields.reduce((map, field) => {
    const isFieldDisabled = !fieldAppliesToComponent(field, data);

    if (map[field.name]) {
      map[field.name].isDisabled =
        map[field.name].isDisabled && isFieldDisabled;
    } else {
      map[field.name] = {
        isDisabled: isFieldDisabled,
        value: coerceFieldValue(field, getFieldValue(field, data), context),
      };
    }
    return map;
  }, fieldMap);
};

export const extractCustomFieldsFromData = (
  fields: APIFieldAttributes[],
  data: APIReferenceAttributes,
  context: TableCustomFieldContext
) => {
  const fieldMap: Record<string, { isDisabled: boolean; value: any }> = {};
  return fields.reduce((map, field) => {
    const isFieldDisabled = !fieldAppliesToReference(field, data);

    if (map[field.name]) {
      map[field.name].isDisabled =
        map[field.name].isDisabled && isFieldDisabled;
    } else {
      map[field.name] = {
        isDisabled: isFieldDisabled,
        value: coerceFieldValue(field, getFieldValue(field, data), context),
      };
    }
    return map;
  }, fieldMap);
};

const cellTypeMap: Record<APIFieldType | 'default', CellTypes> = {
  [APIFieldType.URL]: CellTypes.URI,
  [APIFieldType.EMAIL]: CellTypes.URI,
  [APIFieldType.USER]: CellTypes.USER,
  [APIFieldType.CHECKBOX]: CellTypes.CHECKBOX,
  [APIFieldType.NUMBER]: CellTypes.NUMBER,
  [APIFieldType.TEXT]: CellTypes.TEXT,
  [APIFieldType.DATE_ONLY]: CellTypes.DATE_ONLY,
  [APIFieldType.DATE_TIME]: CellTypes.DATE_TIME,
  [APIFieldType.LIST]: CellTypes.TEXT,
  [APIFieldType.SELECT_MULTIPLE_LIST]: CellTypes.TEXT,
  [APIFieldType.DATE_ONLY_RANGE]: CellTypes.TEXT,
  [APIFieldType.DATE_TIME_RANGE]: CellTypes.TEXT,
  [APIFieldType.TEXT_AREA]: CellTypes.TEXT_AREA,
  [APIFieldType.FILE]: CellTypes.URI,
  default: CellTypes.TEXT,
};

const fieldProps = (field: APIFieldAttributes) => ({
  key: field.name,
  label: field.label,
  cellType: cellTypeMap[field.type] || cellTypeMap.default,
  order: null,
  custom: true,
  global: field.global,
  componentTypes: field.componentType,
  numberFormatOptions: field.numberFormatOptions,
});

const coerceFieldValue = (
  field: APIFieldAttributes,
  value: any,
  { usersById, attachmentsById, i18nLocale }: TableCustomFieldContext
) => {
  switch (field.type) {
    case APIFieldType.URL:
      return value
        ? {
            uri: urlUtils.processUrl(value),
            label: value,
          }
        : { uri: '', label: '' };
    case APIFieldType.EMAIL:
      return value
        ? {
            uri: `mailto:${value}`,
            label: value,
          }
        : { uri: '', label: '' };
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return Array.isArray(value) ? value.join(', ') : value;
    case APIFieldType.USER: {
      const user = usersOperations.getUserById(usersById, value);
      return {
        name:
          user?.name ||
          user?.email ||
          (value ? 'User info missing' : undefined),
        email: user?.email,
        image: user ? getUserImage(usersById, value) : undefined,
      };
    }
    case APIFieldType.FILE: {
      const file = attachmentsById[value];

      if (!file) {
        return { label: 'File info missing', url: '' };
      }

      return { label: file.filename, uri: file.uri };
    }
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return fieldUtils.fieldAttributesToLabel({
        fieldAttributes: field,
        fieldType: field.type,
        value,
        i18nLocale,
        formatUserFieldFn: formatUserFieldValue(usersById),
      });
    default:
      return value;
  }
};

export const updateSort =
  (attr: string, order: number) => (header: HeaderModel) => {
    if (header.key === attr) {
      header.order = order;
    } else {
      header.order = null;
    }
    return header;
  };

export const fieldHeaderProps = (field: APIFieldAttributes) => ({
  ...fieldProps(field),
  isExpandController: false,
});

export const setupHeaders = (sort: ContextSort, headers: HeaderModel[]) =>
  headers.map(updateSort(sort.attr, sort.order));

export const getCellValue = (value: any) =>
  value instanceof Object && 'value' in value ? value.value : value;
